import clsx from 'clsx';

import * as React from 'react';
import './title.scss';

const __DEFAULT_ELEMENT__ = 'div';
type TitleOwnProps<E extends React.ElementType = typeof __DEFAULT_ELEMENT__> = {
  component?: E;
  /**
   * @default 'normal'
   * thin: 100
   * lighter: 200
   * light: 300
   * normal: 400
   * medium: 500
   * semi-bold: 600
   * bold: 700
   * bolder: 800
   */
  weight?:
    | 'thin'
    | 'lighter'
    | 'light'
    | 'normal'
    | 'medium'
    | 'semi-bold'
    | 'bold'
    | 'bolder'
    | 'black';
  /**
   * @default 'lg'
   * lg: fs-lg
   * xl: fs-xl
   * 2xl: fs-2xl
   * 3xl: fs-3xl
   * 4xl: fs-4xl
   */
  heading?: 'page-title' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'secondary-gray'
    | 'tertiary-gray'
    | 'warning'
    | 'danger'
    | 'tertiary-bold';
  wrap?: boolean;
};

type TitleProps<E extends React.ElementType> = TitleOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof TitleOwnProps>;

const InnerTitle = <T extends React.ElementType = typeof __DEFAULT_ELEMENT__>({
  component: Component,
  id,
  isLoading,
  loaderProps,
  children,
  heading = 'lg',
  weight = 'normal',
  color = 'secondary',
  wrap = false,
  className, // className should NOT include any text size/weight classes
  ...rest
}: TitleProps<T>) => {
  const ComponentToUse = Component || __DEFAULT_ELEMENT__;

  return (
    <ComponentToUse
      className={clsx(
        {
          'text-nowrap': !wrap,
          'text-primary': color === 'primary',
          'text-title-color': color === 'secondary',
          'text-tertiary': color === 'tertiary',
          'text-tertiary-bold': color === 'tertiary-bold',
          'text-neutral-grey-6': color === 'secondary-gray',
          'text-danger-text': color === 'danger',
          'text-warning-text': color === 'warning',
          'fs-lg': heading === 'lg',
          'fs-xl': heading === 'xl',
          'fs-xl mb-title': heading === 'page-title',
          'fs-2xl': heading === '2xl',
          'fs-3xl': heading === '3xl',
          'fs-4xl': heading === '4xl',
          'fw-thin': weight === 'thin',
          'fw-lighter': weight === 'lighter',
          'fw-light': weight === 'light',
          'fw-normal': weight === 'normal',
          'fw-medium': weight === 'medium',
          'fw-semi-bold': weight === 'semi-bold',
          'fw-bold': weight === 'bold',
          'fw-bolder': weight === 'bolder',
          'fw-black': weight === 'black',
        },
        className,
      )}
      {...rest}
      id={id}
      data-cy={id}
      data-testid={id}
    >
      {children}
    </ComponentToUse>
  );
};

export default InnerTitle;
